import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldType } from '@ngx-formly/core';
import { FormControl, FormGroup, FormsModule, Validators } from '@angular/forms';
import { ModuleKeys } from '@core/enums/module-keys.enum';
import { Observable } from 'rxjs';
import { OptionsPipe } from '@features/client/form-builder/pipes/options.pipe';
import { shareReplay, tap } from 'rxjs/operators';
import { OrderWorkflowFacade } from '@features/client/order-workflow/facades/order-workflow.facade';
import { Location } from '@features/client/profile/models/location.model';
import { AppRoutes } from '@core/enums/routes.enum';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { RadioButton } from 'primeng/radiobutton';
import { CapturumButtonComponent } from '@capturum/ui/button';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { TranslateDentalPipe } from '@shared/pipes/translate-dental.pipe';
import { CapturumValidatorComponent } from '@capturum/ui/api';

@Component({
  selector: 'app-location-selection-input',
  templateUrl: './location-selection-input.component.html',
  styleUrls: ['./location-selection-input.component.scss'],
  imports: [
    NgTemplateOutlet,
    AsyncPipe,
    RadioButton,
    FormsModule,
    CapturumButtonComponent,
    TranslatePipe,
    RouterLink,
    TranslateDentalPipe,
    CapturumValidatorComponent,
  ],
})
export class LocationSelectionInputComponent extends FieldType implements OnInit {
  public formGroup: FormGroup = new FormGroup({});
  public moduleKeys: typeof ModuleKeys = ModuleKeys;
  public locations$: Observable<Location[]>;
  public appRoutes: typeof AppRoutes = AppRoutes;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly optionsPipe: OptionsPipe,
    private readonly orderWorkflowFacade: OrderWorkflowFacade,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.initForm();
    this.getLocations();
  }

  public getLocations(): void {
    this.locations$ = this.optionsPipe
      .transform({
        type: 'endpoint',
        data: {
          url: `order/${this.orderWorkflowFacade.getOrderSnapshot().id}/location`,
        },
      })
      .pipe(
        tap((locations) => {
          if (locations?.length === 1) {
            this.formGroup.controls.location_id.setValue(locations[0].id);
          }
        }),
        shareReplay(1),
      );
  }

  private initForm(): void {
    this.addLocationControls();
    this.handleLocationIdChange();
  }

  private addLocationControls(): void {
    const locationId =
      typeof this.field?.defaultValue === 'string' ? this.field?.defaultValue : this.field?.defaultValue?.location_id;

    if (Object.prototype.hasOwnProperty.call(this.form.value, 'location_id')) {
      // @todo: // probably an error might arise
      this.form.get('location_id')?.setValue(locationId);
    }

    this.formGroup.addControl('location_id', new FormControl(locationId, Validators.required));
  }

  private handleLocationIdChange(): void {
    this.formGroup
      .get('location_id')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((locationId) => {
        this.form.get('location_id').setValue(locationId);
      });
  }
}
